"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DocumentsClient = void 0;
var models_1 = require("src/models");
var clients_1 = require("src/clients");
var constants_1 = require("../constants");
/**
 * SDK for interacting with documents in AppDB.
 * ### Example Usage
 *
 * ```js
 * import { AppDBClient } from '@domoinc/toolkit';
 *
 * interface Context {
 *   name: string;
 *   age: number;
 * }
 * const appDbClient = new AppDBClient.DocumentsClient<Context>('collectionName');
 *
 * const addRecord = async (name: string, age: number) =>
 *   appDbClient.create({ name, age });
 * ```
 */
var DocumentsClient = /** @class */ (function () {
    function DocumentsClient(collection, typeguard, reCaptchaToken) {
        this.transportClient = clients_1.TransportClient;
        this.collection = collection;
        this.typeguard = typeguard;
        this.reCaptchaHeader =
            reCaptchaToken === undefined
                ? {}
                : {
                    'X-DOMO-Ryuu-Recaptcha': reCaptchaToken,
                };
    }
    /**
     * A helper method to retrieve the documents in the initialized collection
     *
     * @param query Used to determine which documents are included in the results
     */
    DocumentsClient.prototype.get = function (query, queryParams) {
        var params = new URLSearchParams();
        if (queryParams !== undefined) {
            ['groupby', 'orderby', 'avg', 'sum', 'max', 'min'].forEach(function (x) {
                var param = queryParams[x];
                if (param !== undefined) {
                    params.set(x, param.join(','));
                }
            });
            ['count', 'limit', 'offset'].forEach(function (x) {
                var param = queryParams[x];
                if (param !== undefined) {
                    params.set(x, param.toString());
                }
            });
        }
        return this.transportClient.post((0, constants_1.SEARCH_DOCUMENTS)(this.collection), typeof query === 'string'
            ? query
            : JSON.stringify(typeof query !== 'undefined' ? query : {}), __assign({ accept: models_1.DataFormatTypes.json, 'content-type': models_1.DataFormatTypes.json }, this.reCaptchaHeader), undefined, params.toString());
    };
    /**
     * A helper method to create new documents in the collection
     */
    DocumentsClient.prototype.create = function (documents) {
        var url = this.isDocumentTypeArray(documents)
            ? (0, constants_1.BULK_ADD_DOCUMENTS)(this.collection)
            : (0, constants_1.ADD_DOCUMENT)(this.collection);
        var documentPayload = this.isDocumentTypeArray(documents)
            ? documents.map(function (doc) { return ({ content: doc }); })
            : { content: documents };
        return this.transportClient.post(url, JSON.stringify(documentPayload), __assign({ accept: models_1.DataFormatTypes.json, 'content-type': models_1.DataFormatTypes.json }, this.reCaptchaHeader));
    };
    /**
     * A helper method to update all documents matching the given **query** with
     * the given **operation**.
     *
     * @param query Query used to determine which documents are affected by the update
     * @param operation mongodb UpdateQuery operation to be applied on the matching documents
     */
    DocumentsClient.prototype.partialUpdate = function (query, operation) {
        return this.transportClient.put((0, constants_1.PARTIAL_UPDATE)(this.collection), JSON.stringify({
            query: query,
            operation: operation,
        }), __assign({ accept: models_1.DataFormatTypes.json, 'content-type': models_1.DataFormatTypes.json }, this.reCaptchaHeader));
    };
    DocumentsClient.prototype.update = function (documents) {
        var url = Array.isArray(documents)
            ? (0, constants_1.BULK_EDIT_DOCUMENTS)(this.collection)
            : (0, constants_1.EDIT_DOCUMENT)(this.collection, documents.id);
        var documentPayload = Array.isArray(documents)
            ? documents
            : { content: documents.content };
        return this.transportClient.put(url, JSON.stringify(documentPayload), __assign({ accept: models_1.DataFormatTypes.json, 'content-type': models_1.DataFormatTypes.json }, this.reCaptchaHeader));
    };
    DocumentsClient.prototype.delete = function (documents) {
        var url = Array.isArray(documents)
            ? (0, constants_1.BULK_DELETE_DOCUMENTS)(this.collection, documents)
            : (0, constants_1.BULK_DELETE_DOCUMENTS)(this.collection, [documents]);
        return this.transportClient.delete(url, undefined, __assign({ accept: models_1.DataFormatTypes.json }, this.reCaptchaHeader));
    };
    DocumentsClient.prototype.isDocumentTypeArray = function (x) {
        return (Array.isArray(x) &&
            (typeof this.typeguard === 'undefined' || !this.typeguard(x)));
    };
    return DocumentsClient;
}());
exports.DocumentsClient = DocumentsClient;
